import {
  Card,
  Divider,
  List,
  Tag,
  Typography,
  Space,
  Empty,
  Row,
  Col,
} from "antd";
import { useEffect, useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import AddBin from "./AddBin";
import bin_img from "../../../assets/dashboard/bin_img.png";
import ManageBinService from "../../../services/ManageBinService";
import { useNavigate } from "react-router";
import { getUnitApiBaseFour, SUB_URL_wash } from "../../../services/BaseUrl";

const ManageBinDashboard = ({ userData }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [displayBin, setDisplayBin] = useState([]);
  const [filteredBins, setFilteredBins] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");

  const [readyToCollectStats, setReadyToCollectStats] = useState({
    bins: 0,
    pieces: 0,
  });

  const [sentToWashStats, setSentToWashStats] = useState({
    bins: 0,
    pieces: 0,
  });

  const navigate = useNavigate();

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, "0")}.${(
    today.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}.${today.getFullYear()}`;

  const normalizeLocation = (value) =>
    String(value || "").trim().toLowerCase().replace(/\s+/g, "_");

  const totalBins = displayBin.length;

  const totalGsmBins = displayBin.filter(
    (bin) => normalizeLocation(bin.bin_location) === "garment_super_market"
  ).length;

  const totalWashingBins = displayBin.filter(
    (bin) => normalizeLocation(bin.bin_location) === "washing"
  ).length;

  const totalDispatchCenterBins = displayBin.filter(
    (bin) => normalizeLocation(bin.bin_location) === "dispatch_center"
  ).length;

  const totalInTransitBins = displayBin.filter(
    (bin) => normalizeLocation(bin.bin_location) === "in_transit"
  ).length;

  const getUnitCode = () => {
    const unitCode = localStorage.getItem("unit_code");

    if (!unitCode) {
      throw new Error("unit_code missing");
    }

    return unitCode;
  };

  const applyFilter = (key, bins = displayBin) => {
    setActiveFilter(key);

    if (key === "all") {
      setFilteredBins(bins);
      return;
    }

    const filtered = bins.filter(
      (bin) => normalizeLocation(bin.bin_location) === key
    );

    setFilteredBins(filtered);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const unitCode = getUnitCode();

        const binResponse = await ManageBinService.getBinData();
        let binData = binResponse?.data || [];

        const storedUserData = JSON.parse(localStorage.getItem("userData"));

        if (storedUserData && storedUserData.data?.dept === "21") {
          binData = binData.filter(
            (bin) =>
              bin.bin_location === "dispatch_center" && bin.bin_full === "yes"
          );
        }

        setDisplayBin(binData);
        applyFilter(activeFilter, binData);

        const readyFormData = new FormData();
        readyFormData.append("bin_location", "dispatch_center");

        const readyResponse = await fetch(
          `${getUnitApiBaseFour(unitCode)}${SUB_URL_wash.washing_dashboard_cards}`,
          {
            method: "POST",
            body: readyFormData,
          }
        );

        const readyData = await readyResponse.json();

        if (readyData?.status) {
          setReadyToCollectStats({
            bins: readyData.total_full_bins || 0,
            pieces: readyData.total_size_qty_sum || 0,
          });
        }

        const washFormData = new FormData();
        washFormData.append("bin_location", "washing");
        washFormData.append("movement_status", "In Transit");

        const washResponse = await fetch(
          `${getUnitApiBaseFour(unitCode)}${SUB_URL_wash.washing_dashboard_cards}`,
          {
            method: "POST",
            body: washFormData,
          }
        );

        const washData = await washResponse.json();

        if (washData?.status) {
          setSentToWashStats({
            bins: washData.total_full_bins || 0,
            pieces: washData.total_size_qty_sum || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching bin dashboard:", error);
      }
    };

    fetchData();
  }, []);

  const handleAddBin = (binData) => {
    const updatedBins = [...displayBin, binData];

    setDisplayBin(updatedBins);
    applyFilter(activeFilter, updatedBins);
  };

  const handleCardClick = (bin) => {
    navigate(
      `/eol-manage-bin/view-garment?bin_id=${encodeURIComponent(
        bin.bin_id
      )}&bin_location=${encodeURIComponent(bin.bin_location)}`
    );
  };

  const getLocationConfig = (bin) => {
    const key = normalizeLocation(bin?.bin_location);

    switch (key) {
      case "dispatch_center":
        return {
          label: "Dispatch Center",
          bg: "#f3e8ff",
          color: "#6b21a8",
          border: "#d8b4fe",
        };

      case "in_transit":
        return {
          label: "In Transit",
          bg: "#fef9c3",
          color: "#854d0e",
          border: "#fde68a",
        };

      case "washing":
        return {
          label: "Washing",
          bg: "#e0f2fe",
          color: "#075985",
          border: "#bae6fd",
        };

      case "garment_super_market":
        return {
          label: "Garment Super Market",
          bg: "#fce7f3",
          color: "#9d174d",
          border: "#f9a8d4",
        };

      default:
        return {
          label: bin?.bin_location || "Unknown",
          bg: "#f1f5f9",
          color: "#475569",
          border: "#cbd5e1",
        };
    }
  };

  const stats = [
    {
      label: "Total Bins",
      value: totalBins,
      color: "#475569",
      key: "all",
    },
    {
      label: "Dispatch Center Bins",
      value: totalDispatchCenterBins,
      color: "#6b21a8",
      key: "dispatch_center",
    },
    {
      label: "Washing Bins",
      value: totalWashingBins,
      color: "#075985",
      key: "washing",
    },
    {
      label: "GSM Bins",
      value: totalGsmBins,
      color: "#9d174d",
      key: "garment_super_market",
    },
    {
      label: "In Transit Bins",
      value: totalInTransitBins,
      color: "#854d0e",
      key: "in_transit",
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title-wrap">
          <Typography.Title level={3} className="page-title">
            Manage Bin
          </Typography.Title>

          <Typography.Text className="page-subtitle">
            View, track, and manage bins across locations
          </Typography.Text>
        </div>
      </div>

      <Divider className="page-divider" />

      <div className="compact-stats" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 12]} justify="center">
          {stats.map((item) => {
            const isActive = activeFilter === item.key;

            return (
              <Col xs={12} sm={8} md={8} lg={4} key={item.key}>
                <div
                  className="compact-stat-item"
                  onClick={() => applyFilter(item.key)}
                  style={{
                    cursor: "pointer",
                    border: isActive
                      ? `2px solid ${item.color}`
                      : "2px solid transparent",
                    borderRadius: 12,
                    transition: "all 0.2s ease",
                    boxShadow: isActive
                      ? "0 8px 22px rgba(15, 23, 42, 0.12)"
                      : undefined,
                    transform: isActive ? "translateY(-2px)" : "none",
                  }}
                >
                  <div
                    className="compact-stat-value"
                    style={{ color: item.color }}
                  >
                    {item.value}
                  </div>
                  <div className="compact-stat-label">{item.label}</div>
                </div>
              </Col>
            );
          })}
        </Row>
      </div>

      <Card
        className="modern-card"
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <InboxOutlined />
            <Typography.Text strong>
              Bin Overview
              {activeFilter !== "all" ? ` - ${filteredBins.length} Bins` : ""}
            </Typography.Text>
          </Space>
        }
        extra={
          <Tag color="blue" className="filter-tag">
            Date: {formattedDate}
          </Tag>
        }
      >
        <AddBin
          userData={userData}
          addBinModal={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onAddBin={handleAddBin}
        />

        <div style={{ marginTop: 8 }}>
          {filteredBins.length > 0 ? (
            <List
              grid={{
                gutter: 16,
                xs: 1,
                sm: 2,
                md: 3,
                lg: 4,
                xl: 5,
              }}
              dataSource={filteredBins}
              renderItem={(bin) => {
                const locationConfig = getLocationConfig(bin);

                return (
                  <List.Item>
                    <div
                      className="bin-card"
                      onClick={() => handleCardClick(bin)}
                    >
                      <div className="bin-card-top">
                        <span
                          className="location-badge"
                          style={{
                            background: locationConfig.bg,
                            color: locationConfig.color,
                            borderColor: locationConfig.border,
                          }}
                        >
                          {locationConfig.label}
                        </span>
                      </div>

                      <div className="bin-card-image-wrap">
                        <img
                          src={bin_img}
                          alt="bin"
                          className="bin-card-image"
                        />

                        <div className="bin-card-name">
                          <strong>{bin.bin_name}</strong>
                        </div>
                      </div>

                      <div className="bin-card-footer">
                        <div
                          className={`pcs-badge ${
                            bin.bin_full === "yes"
                              ? "full-badge"
                              : "empty-badge"
                          }`}
                        >
                          {bin.total_quantity || 0} Pcs
                        </div>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          ) : (
            <Empty description="No bins found for selected category" />
          )}
        </div>
      </Card>
    </div>
  );
};

export default ManageBinDashboard;